<script setup>
import { computed, getCurrentInstance } from 'vue';
import { usePage } from '@inertiajs/vue3';
import { useLayout } from '@/layout/common/composables/layout';
import ClientDashboardBreadcrumb from '@/layout/client/ClientDashboardBreadcrumb.vue';
import ClientDashboardHeader from '@/layout/client/ClientDashboardHeader.vue';
import ClientDashboardSidebar from '@/layout/client/ClientDashboardSidebar.vue';
import GlobalLoadingOverlay from '@/components/GlobalLoadingOverlay.vue';
import AnnouncementModal from '@/components/AnnouncementModal.vue';

const { menuMode } = useLayout();
const page = usePage();
const { proxy } = getCurrentInstance();
const $t = proxy.$t;

const isImpersonating = computed(() => page.props?.auth?.user?.is_impersonated === true);
const impersonatedName = computed(() => {
    const u = page.props?.auth?.user || {};
    return u.name || [u.first_name, u.last_name].filter(Boolean).join(' ').trim() || u.email || '';
});

function leaveImpersonation() {
    // Full navigation: leaving impersonation logs out the `client` guard and
    // changes the session, so a hard redirect (not an Inertia visit) is safest.
    // Return to the admin dashboard (the impersonator's admin session is restored
    // by leave()), not the client login page.
    window.location.href = route('impersonate.leave', ['dashboard']);
}
</script>

<template>
    <div class="h-dvh bg-primary-50 dark:bg-surface-950 relative p-2 flex overflow-hidden" :class="menuMode === 'horizontal' ? 'flex-col' : 'flex-row'">
        <ClientDashboardSidebar />
        <main class="flex-1 max-w-full w-full mx-5 flex flex-col transition-all duration-300 h-full bg-background rounded-3xl shadow-stroke overflow-hidden">
            <div v-if="isImpersonating" class="bg-amber-500 text-white px-4 py-2 flex items-center justify-between gap-3 text-sm">
                <span>
                    {{ $t('You are impersonating') }} <b>{{ impersonatedName }}</b>
                </span>
                <Button :label="$t('Leave impersonation')" size="small" severity="contrast" @click="leaveImpersonation" />
            </div>
            <ClientDashboardHeader />
            <ClientDashboardBreadcrumb />
            <div class="p-6 flex-1 overflow-auto">
                <slot />
            </div>
        </main>
        <GlobalLoadingOverlay />
        <AnnouncementModal />
    </div>
</template>
